import { driver } from "driver.js";
import "driver.js/dist/driver.css";

const tourSteps = [
  {
    element: ".tour-step-1",
    popover: {
      title: "Menu de Serviços",
      description: "No menu superior, você encontra todas as ferramentas para facilitar sua rotina.\n Aqui, você tem autonomia para atualizar seu cadastro, gerenciar benefícios e emitir declarações oficiais de forma ágil e digital.",
      side: "bottom",
      align: 'start'
    }
  },
  {
    element: "#tour-step-2",
    popover: {
      title: "Gestão Financeira",
      description: "Esta área é dedicada ao acompanhamento detalhado da sua produção médica.\n\nConsulte seus honorários, extratos, informes de rendimento e realize a revisão de contas e glosas com total clareza e segurança.",
      side: "bottom",
      align: 'start'
    }
  },
  {
    element: "#tour-step-3",
    popover: {
      title: "Apoio à sua Atuação Médica",
      description: "Utilize nossos sistemas digitais gratuitos para facilitar seus agendamentos e atendimentos.\n\nAlém de otimizar sua rotina, o uso de serviços como o <strong>Hub de Agendamento</strong> gera um acréscimo direto em seus honorários através do nosso programa de remuneração variável.",
      side: "bottom",
      align: 'start'
    }
  },
  {
    element: "#tour-step-4",
    popover: {
      title: "Conexão e Conhecimento",
      description: "Este é o seu espaço de atualização e troca de informações. Aqui, disponibilizamos comunicados oficiais, artigos técnicos e materiais de apoio para fortalecer sua prática médica e o vínculo com a cooperativa.",
      side: "top",
      align: 'start'
    }
  },
  {
    element: "#tour-step-5",
    popover: {
      title: "Experiência Simples e Eficiente",
      description: "Nosso objetivo é garantir que você utilize todos os recursos para apoiar sua jornada.\n\nSe precisar de ajuda, utilize o ícone (?) ou fale com nossa equipe de Relacionamento pelo WhatsApp: \n<strong>(48) 99154-8297</strong>.",
      side: "bottom",
      align: 'end'
    }
  }
];

/**
 * Cria um elemento proxy invisível que cobre a área combinada dos seletores fornecidos.
 * Permite ao driver.js destacar apenas a região desejada sem alterar o HTML.
 */
function createProxyElement(selectors) {
  const elements = selectors.map(s => document.querySelector(s)).filter(Boolean);
  if (elements.length === 0) return null;

  const rects = elements.map(el => el.getBoundingClientRect());
  const left = Math.min(...rects.map(r => r.left));
  const top = Math.min(...rects.map(r => r.top));
  const right = Math.max(...rects.map(r => r.right));
  const bottom = Math.max(...rects.map(r => r.bottom));

  const proxy = document.createElement('div');
  proxy.setAttribute('data-tour-proxy', 'true');
  proxy.style.cssText = [
    'position: fixed',
    `left: ${left}px`,
    `top: ${top}px`,
    `width: ${right - left}px`,
    `height: ${bottom - top}px`,
    'pointer-events: none',
    'z-index: -9999',
    'background: transparent',
  ].join(';');
  document.body.appendChild(proxy);
  return proxy;
}

function removeProxyElements() {
  document.querySelectorAll('[data-tour-proxy]').forEach(el => el.remove());
}

function lockScroll() {
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  const appBg = document.querySelector('.app-background');
  if (appBg) appBg.style.overflow = 'hidden';
}

function unlockScroll() {
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
  const appBg = document.querySelector('.app-background');
  if (appBg) appBg.style.overflow = '';
}

export function startGuidedTour(onComplete, onBackFromFirstStep) {
  // O tour é exibido apenas quando o menu está no modo desktop (Bootstrap breakpoint lg ≥ 992px)
  const BOOTSTRAP_LG = 992;
  if (window.innerWidth < BOOTSTRAP_LG) {
    if (onComplete) onComplete(true); // considera concluído para não bloquear fluxos dependentes
    return;
  }

  // Bloqueia o scroll enquanto o tour estiver ativo
  lockScroll();

  // Passo 1: proxy cobre apenas nav + perfil do usuário (exclui a logo)
  const step1Proxy = createProxyElement(['.tour-step-1', '.profile-container']);

  const steps = [
    {
      element: step1Proxy || '.tour-step-1',
      popover: {
        ...tourSteps[0].popover,
        // Mostra o botão Voltar no passo 1 para permitir retornar ao modal de boas-vindas
        showButtons: ['next', 'previous', 'close'],
      }
    },
    ...tourSteps.slice(1),
  ];

  const driverObj = driver({
    showProgress: true,
    progressText: '{{current}} / {{total}}',
    allowClose: true,
    overlayColor: 'rgba(0, 0, 0, 0.7)',
    nextBtnText: 'Próximo',
    prevBtnText: 'Voltar',
    doneBtnText: 'Concluir',
    steps,
    onHighlighted: (element, step, options) => {
      // Driver.js sempre desabilita o botão Voltar no primeiro passo — removemos manualmente
      if (options.state.activeIndex === 0) {
        const prevBtn = document.querySelector('.driver-popover-prev-btn');
        if (prevBtn) {
          prevBtn.removeAttribute('disabled');
          prevBtn.classList.remove('driver-popover-btn-disabled');
        }
      }
    },
    onNextClick: (element, step, options) => {
      // Remove o proxy ao sair do passo 1
      if (options.state.activeIndex === 0) {
        removeProxyElements();
      }
      driverObj.moveNext();
    },
    onPrevClick: (element, step, options) => {
      const index = options.state.activeIndex;

      // Passo 1 → Voltar reabre o modal de boas-vindas
      if (index === 0) {
        removeProxyElements();
        unlockScroll();
        driverObj.destroy();
        if (onBackFromFirstStep) onBackFromFirstStep();
        return;
      }

      // Passo 2 → Passo 1: recria o proxy e atualiza o step 0 (não o step ativo)
      if (index === 1) {
        removeProxyElements();
        const newProxy = createProxyElement(['.tour-step-1', '.profile-container']);
        if (newProxy) {
          steps[0].element = newProxy;
        }
      }

      driverObj.movePrevious();
    },
    onDestroyStarted: () => {
      removeProxyElements();
      unlockScroll();
      if (driverObj.hasNextStep()) {
        driverObj.destroy();
        if(onComplete) onComplete(false); // skipped
      } else {
        driverObj.destroy();
        if(onComplete) onComplete(true); // finished
      }
    },
  });

  driverObj.drive();
}
